import axios from 'axios';
import {useState} from 'react';
import {v4 as uuid} from 'uuid';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useProcessEvent from '@contentlab/hooks/analytics/use-process-event';
import {
  CONTENT_STUDIO_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import contentStudioInstance from '@/utils/axiosContentStudio';
import downloadAssets from '@/utils/content-studio/downloadAssets';
import {raiseContentStudioError} from '@/utils/contentStudioErrorHandling';
import translate from '@/utils/translate';
import {ROUTES} from '@/config/routesConstants';
import {ActionItem} from '@/views/content-studio/components/asset-actions-bar/ActionsDisplay';
import {Asset} from '@/views/content-studio/components/asset-table/types';
import useDeleteAsset from '@/hooks/content-studio/mutations/use-delete-asset';
import useNavigation from '@/hooks/use-navigation/useNavigation';
import {contentStudioActions, ErrorKind} from '@/stores/contentStudioStore';

const MULTIPLE_ASSETS_ENDPOINT = '/api/v1/assets/download-zip';

function useAssetBarActions(selectedAssets: Asset[]): {
  actions: ActionItem[];
  folderPopperElement: HTMLElement | null;
  setFolderPopperElement: (element: HTMLElement | null) => void;
  renamePopperElement: HTMLElement | null;
  setRenamePopperElement: (element: HTMLElement | null) => void;
} {
  const {navigate} = useNavigation();
  const [folderPopperElement, setFolderPopperElement] =
    useState<HTMLElement | null>(null);
  const [renamePopperElement, setRenamePopperElement] =
    useState<HTMLElement | null>(null);
  const {mutateAsync: deleteAssets} = useDeleteAsset();
  const {started, ended} = useProcessEvent();
  const {uiInteraction} = useUiInteractionEvent();
  const anyAssetNotReady = selectedAssets.some(
    (asset) => asset.status !== 'ready'
  );
  const actions = [
    {
      id: 'rename',
      icon: 'Navigation-Edit',
      tooltip: translate('Rename'),
      isDisabled: selectedAssets.length > 1 || anyAssetNotReady,
      onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        uiInteraction({
          item_name: 'rename',
          interaction_location: CONTENT_STUDIO_VIEW_NAMES.ASSETS,
          flow_name: FLOW_NAMES.CONTENT_STUDIO.ASSETS,
          asset_id: selectedAssets[0].id,
        });

        e.stopPropagation();
        e.preventDefault();
        setRenamePopperElement(e.currentTarget);
      },
    },
    {
      id: 'download',
      icon: 'Navigation-Download',
      tooltip: translate('Download'),
      isDisabled: selectedAssets.length === 0 || anyAssetNotReady,
      onClick: async () => {
        const interactionID = uuid();
        const processID = uuid();
        const endpoint =
          selectedAssets.length === 1
            ? selectedAssets[0].url || ''
            : MULTIPLE_ASSETS_ENDPOINT;
        uiInteraction({
          item_name: 'download',
          interaction_location: CONTENT_STUDIO_VIEW_NAMES.ASSETS,
          flow_name: FLOW_NAMES.CONTENT_STUDIO.ASSETS,
          interaction_id: interactionID,
          asset_id: selectedAssets[0].id,
        });
        selectedAssets.forEach((asset) => {
          started({
            endpoint,
            process_id: processID,
            process_name: 'download',
            original_id: asset.originalAsset?.id || asset.id,
            interaction_id: interactionID,
            asset_id: asset.id,
            process_details: JSON.stringify({
              duration: asset.metadata.durationMillis,
              width: asset.metadata.width,
              height: asset.metadata.height,
              size: asset.metadata.fileSizeBytes,
            }),
            source: CONTENT_STUDIO_VIEW_NAMES.ASSETS,
          });
        });
        let result = '';
        let error = '';
        try {
          if (selectedAssets.length === 1) {
            const response = await axios.get(selectedAssets[0].url || '', {
              responseType: 'blob',
            });
            downloadAssets(response, selectedAssets[0].name || '');
            contentStudioActions.setSelectedAssets([]);
          } else {
            const response = await contentStudioInstance.post(
              MULTIPLE_ASSETS_ENDPOINT,
              [...selectedAssets.map((asset) => asset.id)],
              {
                responseType: 'blob',
              }
            );
            downloadAssets(response, 'assets.zip');
            result = 'success';
            error = '';
          }
        } catch (err) {
          raiseContentStudioError(
            translate('Failed to download assets'),
            ErrorKind.FAILURE
          );
          result = 'failure';
          error = err instanceof Error ? err.message : String(err);
        } finally {
          selectedAssets.forEach((asset) => {
            ended({
              endpoint,
              outcome_id: asset.id,
              original_id: asset.originalAsset?.id || asset.id,
              result,
              process_id: processID,
              process_name: 'download',
              error,
            });
          });
        }
      },
    },
    {
      id: 'folder',
      icon: 'Navigation-Projects',
      tooltip: translate('Move to'),
      onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        uiInteraction({
          item_name: 'move',
          interaction_location: CONTENT_STUDIO_VIEW_NAMES.ASSETS,
          flow_name: FLOW_NAMES.CONTENT_STUDIO.ASSETS,
        });

        e.stopPropagation();
        e.preventDefault();
        setFolderPopperElement(e.currentTarget);
      },
    },
    {
      id: 'delete',
      icon: 'Actions-Remove',
      tooltip: translate('Delete'),
      onClick: async () => {
        uiInteraction({
          item_name: 'delete',
          interaction_location: CONTENT_STUDIO_VIEW_NAMES.ASSETS,
          flow_name: FLOW_NAMES.CONTENT_STUDIO.ASSETS,
          interaction_details: selectedAssets
            .map((asset) => asset.name)
            .join(', '),
        });

        try {
          const fileNames = selectedAssets
            .map((asset) => asset.name)
            .filter(Boolean) as string[];
          await deleteAssets(selectedAssets.map((asset) => asset.id));
          contentStudioActions.setSelectedAssets([]);
          contentStudioActions.removeFiles(fileNames);
        } catch (err) {
          raiseContentStudioError(
            translate('Failed to delete assets'),
            ErrorKind.FAILURE
          );
        }
      },
    },
    {
      id: 'divider',
      isDivider: true,
    },
    {
      id: 'resize',
      icon: 'Actions-Expand',
      tooltip: translate('Resize'),
      isDisabled: anyAssetNotReady,
      onClick: async () => {
        uiInteraction({
          item_name: 'resize',
          interaction_location: CONTENT_STUDIO_VIEW_NAMES.ASSETS,
          flow_name: FLOW_NAMES.CONTENT_STUDIO.ASSETS,
          interaction_details: selectedAssets
            .map((asset) => asset.name)
            .join(', '),
        });
        navigate(ROUTES.CONTENT_LAB_REFORMAT);
      },
    },
    {
      id: 'dub',
      icon: 'Navigation-Messages',
      tooltip: translate('Dub'),
      isDisabled: anyAssetNotReady,
      onClick: async () => {
        uiInteraction({
          item_name: 'dub',
          interaction_location: CONTENT_STUDIO_VIEW_NAMES.ASSETS,
          flow_name: FLOW_NAMES.CONTENT_STUDIO.ASSETS,
          interaction_details: selectedAssets
            .map((asset) => asset.name)
            .join(', '),
        });
        navigate(ROUTES.CONTENT_LAB_TRANSLATE);
      },
    },
    {
      id: 'rephrase',
      icon: 'Features-Rename',
      tooltip: translate('Rescript'),
      isDisabled: selectedAssets.length > 1 || anyAssetNotReady,
      onClick: () => {
        uiInteraction({
          item_name: 'rephrase',
          interaction_location: CONTENT_STUDIO_VIEW_NAMES.ASSETS,
          flow_name: FLOW_NAMES.CONTENT_STUDIO.ASSETS,
          asset_id: selectedAssets[0].id,
        });
        if (selectedAssets.length === 1) {
          navigate(
            `${ROUTES.CONTENT_LAB_REPHRASE}?assetId=${selectedAssets[0].id}`
          );
        } else {
          raiseContentStudioError(
            translate('Rephrase is not supported for multiple assets'),
            ErrorKind.FAILURE
          );
        }
      },
    },
  ];
  const filteredActions = selectedAssets.find(
    (asset) => asset.type === 'upload'
  )
    ? actions.filter((action) => action.id !== 'folder')
    : actions;

  return {
    actions: filteredActions as ActionItem[],
    folderPopperElement,
    setFolderPopperElement,
    renamePopperElement,
    setRenamePopperElement,
  };
}

export default useAssetBarActions;
